'use client';

import { useState, useEffect } from 'react';
import { AutoSizer } from 'react-virtualized';
import { useTranslations, useLocale } from 'next-intl';
import { usePathname, useParams, useSearchParams } from 'next/navigation';

import { Tabs } from './Filters/Tabs';
import { FiltersModal } from './Filters/Filters';
import { ListComponent, DEFAULT_WIDTH } from './List';
import ErrorPage from '@/app/[locale]/error';
import { hasMap } from '@/utils';
import { Loader } from '@/components/base/Layout/Loader';
import { SearchListingMobile } from '@/components/base/SearchListing';
import ScreenLoader from '@/component/ScreenLoader';
import { useSearchState } from './hooks';
import { ListingsMapList } from './ListingsMapList';
import { ListingsListHeader } from './ListHeader';
import { ListingsPlaceholder } from './Placeholder';
import { ListingsInexact } from './Inexact';
import { ListingsSubheader } from './Subheader';
import { useRouter, Link } from '@/i18n/routing';

import styles from './Listing.module.css';
import { ShowBar } from './Filters/ShowBar';

export const ListingsList = ({ initialData, nextPage, disableScrollLoad }) => {
  const [mapPending, setMapPending] = useState(false);
  const t = useTranslations();
  const locale = useLocale();
  const { push } = useRouter();
  const query = useParams();
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const asPath = `${pathname}?${searchParams.toString()}`;

  const showMap = !!(pathname.indexOf('/map') + 1);
  const [, params = ''] = asPath.split('?');

  const close = () => {
    setMapPending(true);
    push(
      asPath
        .replace(`/${locale}`, '')
        .replace('/map', `${query.tab ? `/filters/${query.tab}` : ''}`),
      undefined,
      { shallow: true }
    );
  };

  const searchQuery = { ...query };
  searchParams.forEach((value, key) => (searchQuery[key] = value));

  const {
    searchState,
    variables,
    loading: isSearchStateIniting,
  } = useSearchState(searchQuery);
  const tab = searchState?.tab;

  const filtersModal = (
    <FiltersModal key={JSON.stringify(searchState)} searchState={searchState} variables={variables}>
      {({ openModal }) => (
        <ShowBar
          openModal={openModal}
          searchState={searchState}
          dataTestId="search-filter-button"
        />
      )}
    </FiltersModal>
  );

  useEffect(() => {
    setMapPending(false);
  }, [showMap]);

  return (
    <>
      <AutoSizer
        className="!w-full flex-1 flex flex-col max-sm:pt-[69px] sm:pt-[91px]"
        disableHeight={true}
        defaultWidth={DEFAULT_WIDTH}
      >
        {({ width }) => (
          <>
            <ListingsSubheader>
              <SearchListingMobile filters={filtersModal} />
            </ListingsSubheader>

            <div>
              <div className={`${styles.wrap}`}>
                <div className="mx-auto max-sm:pr-0 max-sm:gap-0 bg-white sm:container border-b border-slate-100 sm:flex justify-between 3xl:rounded-t-10 3xl:mb-3 flex gap-[14px]">
                  <div className="relative sm:-ml-8 flex max-sm:gap-2 sm:gap-[14px] overflow-auto no-scrollbar max-sm:after:min-w-4 sm:after:min-w-10 sm:after:-ml-5 after:h-[52px] after:sticky max-sm:after:right-0 sm:after:right-0 after:bg-gradient-to-r after:from-white/0 after:to-white max-sm:before:min-w-4 sm:before:min-w-10 max-sm:before:-mr-2 sm:before:-mr-5 before:h-[52px] before:sticky before:z-[1] before:left-0 before:bg-gradient-to-l before:from-white/0 before:to-white">
                    <Tabs tab={tab} />
                  </div>
                  <div className="flex gap-[14px] sm:py-2">
                    <div
                      className="max-sm:hidden"
                      data-testid="search-filter-container"
                    >
                      {filtersModal}
                    </div>
                    {!hasMap(variables) ? null : !showMap ? (
                      <Link
                        id="sub-btn"
                        href={`/listings/map?${params}`}
                        onClick={() => setMapPending(true)}
                        className="btn relative max-sm:btn-primary max-sm:opacity-90 max-sm:hover:text-white max-sm:rounded-20 max-sm:fixed max-sm:bottom-2.5 max-sm:left-1/2 max-sm:-translate-x-1/2 max-sm:z-10 max-sm:!py-[14px] max-sm:!pl-[22px] max-sm:!pr-[30px] sm:btn-second btn-sm btn-icon self-end gap-2 !pr-2.5 sm:focus:ring-primary-800 sm:focus:text-primary-800"
                      >
                        <i
                          className={`${
                            mapPending && 'opacity-0'
                          } icon-map text-[24px]`}
                          aria-hidden="true"
                        />
                        <span
                          className={`${
                            mapPending && 'opacity-0'
                          } whitespace-nowrap`}
                        >
                          {t('listings.map.show')}
                        </span>
                        {mapPending && <Loader />}
                      </Link>
                    ) : (
                      <button
                        id="sub-btn"
                        onClick={close}
                        className="btn relative max-sm:btn-primary max-sm:opacity-90 max-sm:hover:text-white max-sm:rounded-20 max-sm:fixed max-sm:bottom-2.5 max-sm:left-1/2 max-sm:-translate-x-1/2 max-sm:z-10 max-sm:!py-[14px] max-sm:!pl-[22px] max-sm:!pr-[30px] sm:btn-second sm:!bg-primary-50 btn-sm btn-icon self-end gap-2 !pr-2.5 sm:focus:ring-primary-800 sm:focus:text-primary-800"
                      >
                        <i
                          className={`${
                            mapPending && 'opacity-0'
                          } icon-map_close text-[24px]`}
                          aria-hidden="true"
                        />
                        <span
                          className={`${
                            mapPending && 'opacity-0'
                          } whitespace-nowrap`}
                        >
                          {t('listings.map.hide')}
                        </span>
                        <span
                          className={`${
                            mapPending && 'opacity-0'
                          } max-sm:hidden absolute top-1 right-1 w-2 h-2 bg-rose-600 rounded-10 flex-col justify-center items-center inline-flex`}
                        />
                        {mapPending && <Loader />}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <ListComponent
              key={JSON.stringify(searchState) + locale}
              query={query}
              initialData={initialData?.availableListings}
              nextPage={nextPage}
              disableScrollLoad={disableScrollLoad}
              width={width}
              variables={variables}
              searchState={searchState}
              isSearchStateIniting={isSearchStateIniting}
            >
              {({
                list,
                location,
                totalCount,
                isScreenLoading,
                isListVisible,
                isEmpty,
                isError,
                isPlaceholderNeed,
                isInexactNeed,
              }) =>
                isError ? (
                  <ErrorPage />
                ) : (
                  <>
                    {isScreenLoading && !query?.map && <ScreenLoader />}

                    <div>
                      {isListVisible && (
                        <div
                          className={`container 3xl:rounded-t-10 3xl:mt-3 sm:pt-[60px]`}
                        >
                          <ListingsListHeader>
                            {totalCount > 300 ? '300+' : `${totalCount}`}{' '}
                            <span className="lowercase">
                              {t(
                                `listings.result.title${
                                  totalCount === 1 ? '-one' : ''
                                }`
                              )}
                            </span>{' '}
                            {t('listings.result.title-after')}{' '}
                            {location?.name || 'Sweden'}
                          </ListingsListHeader>
                        </div>
                      )}
                    </div>

                    <section
                      className={`container flex flex-col max-sm:pt-7 pt-8 max-sm:pb-10 pb-16 min-h-full flex-1 lg:max-list-3xl:!px-[52px] sm:max-lg:!px-[28px] 3xl:rounded-b-10 ${
                        !isListVisible ? '3xl:rounded-10 3xl:mt-3' : ''
                      }`}
                    >
                      <div
                        className={isListVisible ? '' : '3xl:mt-3 sm:pt-[60px]'}
                      >
                        {list}
                      </div>
                      {isPlaceholderNeed && (
                        <ListingsPlaceholder
                          isEmpty={isEmpty}
                          searchState={searchState}
                          variables={variables}
                          query={query}
                          width={width}
                        />
                      )}
                      {isInexactNeed && (
                        <ListingsInexact
                          isEmpty={isEmpty}
                          query={query}
                          searchState={searchState}
                          width={width}
                        />
                      )}
                    </section>

                    <ListingsMapList
                      open={showMap}
                      close={close}
                      variables={variables}
                      totalCount={totalCount}
                    />
                  </>
                )
              }
            </ListComponent>
          </>
        )}
      </AutoSizer>
    </>
  );
};
